import React, { useEffect, useState } from "react";
import { fetchPlans, createSubscription, getCheckoutStatus } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RedeemCodePanel } from "./RedeemCodePanel";
import LEGAL_CONFIG from "../legal/legal.config";

// Persist the user's legal-consent decision across sessions so they
// don't re-tick the box on every visit. Bump the version when the
// underlying legal docs change materially so consent is re-requested.
const LEGAL_CONSENT_KEY = "lh.stripe.legal.consent.v1";
function readLegalConsent() {
  try {
    const raw = localStorage.getItem(LEGAL_CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeLegalConsent(payload) {
  try { localStorage.setItem(LEGAL_CONSENT_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
}

/**
 * Modal that gates every Stripe Checkout redirect. Required checkbox +
 * disclosure copy under Canadian consumer-protection norms; the
 * "express consent to immediate delivery" line waives the carve-out
 * for the digital-services cooling-off period (lawyer to finalize the
 * exact wording per /app/LEGAL_SETUP.md).
 */
function CheckoutConsentModal({ plan, onCancel, onConfirm }) {
  const isPlayer = plan?.plan_id === "player-pro";
  const cfg = isPlayer
    ? LEGAL_CONFIG.pricing.pro_player
    : LEGAL_CONFIG.pricing.pro_league;
  const [legalChecked, setLegalChecked] = useState(false);
  const [immediateChecked, setImmediateChecked] = useState(false);
  if (!plan) return null;
  const planLabel = isPlayer ? "PRO Player" : "PRO League";
  return (
    <div
      data-testid="checkout-consent-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[140] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
    >
      <div className="max-w-md w-full rounded-3xl border border-[#14b8a6]/40 bg-black/95 p-6 shadow-[0_0_60px_-15px_rgba(20,184,166,0.6)]">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6]">CONFIRM SUBSCRIPTION</p>
        <h3 className="text-2xl font-display grad-text mt-2">{planLabel}</h3>
        <p className="mt-3 text-sm text-white/85">
          <strong className="text-[#14b8a6]">
            {cfg.amount} {cfg.currency} / {cfg.interval}
          </strong>{" "}
          — auto-renews until cancelled. Cancel anytime in{" "}
          <em>Profile → Billing → Manage subscription</em>.
        </p>
        <p className="mt-2 text-[11px] text-white/55">
          Taxes (e.g. GST/HST) may apply at checkout depending on your
          location.
        </p>

        <div className="mt-5 space-y-3 text-[12px] text-white/80">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={legalChecked}
              onChange={(e) => setLegalChecked(e.target.checked)}
              data-testid="checkout-consent-legal-checkbox"
              className="mt-0.5 accent-[#14b8a6]"
            />
            <span>
              I have read and agree to the{" "}
              <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="underline text-[#14b8a6]">Terms of Service</a>,{" "}
              <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="underline text-[#14b8a6]">Privacy Policy</a>, and{" "}
              <a href="/legal/subscriptions" target="_blank" rel="noopener noreferrer" className="underline text-[#14b8a6]">Subscription Terms</a>.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={immediateChecked}
              onChange={(e) => setImmediateChecked(e.target.checked)}
              data-testid="checkout-consent-immediate-checkbox"
              className="mt-0.5 accent-[#14b8a6]"
            />
            <span>
              I expressly consent to immediate delivery of the digital
              service and acknowledge that this may limit my statutory
              cancellation right for the portion already accessed. See{" "}
              <a href="/legal/withdrawal" target="_blank" rel="noopener noreferrer" className="underline text-[#14b8a6]">Cancellation &amp; Refunds</a>.
            </span>
          </label>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            data-testid="checkout-consent-cancel"
            className="flex-1 py-2.5 rounded-xl bg-white/8 hover:bg-white/15 text-white text-[11px] tracking-widest"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={() => {
              writeLegalConsent({
                ts: new Date().toISOString(),
                plan_id: plan.plan_id,
                versions: LEGAL_CONFIG.versions,
              });
              onConfirm();
            }}
            disabled={!legalChecked || !immediateChecked}
            data-testid="checkout-consent-confirm"
            className={`flex-1 py-2.5 rounded-xl text-[11px] tracking-widest font-bold ${
              legalChecked && immediateChecked
                ? "bg-[#14b8a6] text-black hover:bg-[#10a39a]"
                : "bg-white/8 text-white/30 cursor-not-allowed"
            }`}
          >
            CONTINUE TO STRIPE
          </button>
        </div>
      </div>
    </div>
  );
}

function StripeLiveChip({ visible = true }) {
  if (!visible) return null;
  return (
    <span className="absolute top-2 right-3 text-[9px] tracking-widest bg-black/70 text-[#14b8a6] px-2 py-0.5 rounded-full border border-[#14b8a6]/40">
      STRIPE LIVE
    </span>
  );
}

function PlanCard({ plan, isCurrent, onUpgrade, numeral, icon }) {
  const isFree = plan.price_usd === 0;
  return (
    <div
      data-testid={`plan-card-${plan.plan_id}`}
      className={`card-3d glass rounded-3xl p-7 relative ${
        isFree
          ? "border border-[#14b8a6]/40 hover:border-[#14b8a6]"
          : "border-2 border-[#14b8a6] neon-glow-pink"
      }`}
    >
      {!isFree && (
        <div
          data-testid={`plan-badge-${plan.plan_id}`}
          className="absolute top-5 right-5 bg-[#14b8a6] text-black font-display text-[10px] tracking-[3px] px-4 py-1.5 rounded-full animate-glow-pulse"
        >
          ${plan.price_usd}/MO
        </div>
      )}

      <div className="flex items-start justify-between gap-4">
        <div className={`text-6xl font-display ${isFree ? "text-[#14b8a6]" : "text-[#14b8a6]"} leading-none`}>
          {numeral}
        </div>
        <div className="text-right">
          <span
            className={`inline-block px-3 py-1 text-[10px] font-bold tracking-[2px] rounded-full ${
              isFree ? "bg-[#14b8a6]/15 text-[#14b8a6]" : "bg-[#14b8a6]/15 text-[#14b8a6]"
            }`}
          >
            {plan.audience.toUpperCase()} TIER
          </span>
          <h3 className="text-3xl font-display grad-text mt-2 leading-tight">{plan.name}</h3>
          <p className="text-3xl font-display mt-2">
            {isFree ? (
              <span className="text-[#14b8a6]">FREE</span>
            ) : (
              <span className="text-[#14b8a6]">${plan.price_usd}<span className="text-sm align-top opacity-70">/mo</span></span>
            )}
          </p>
        </div>
      </div>

      <p className="mt-4 text-sm text-[#a3a3ac] italic">{plan.tagline}</p>

      <ul className="mt-5 space-y-2.5 text-sm">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <i className={`fas fa-check-circle mt-1 ${isFree ? "text-[#14b8a6]" : "text-[#14b8a6]"}`} />
            <span dangerouslySetInnerHTML={{ __html: f }} />
          </li>
        ))}
      </ul>

      <div className="mt-7 space-y-2">
        {isCurrent ? (
          <button
            disabled
            data-testid={`plan-current-${plan.plan_id}`}
            className="w-full py-3 rounded-2xl bg-white/10 border border-[#14b8a6]/40 text-white font-bold"
          >
            CURRENT PLAN
          </button>
        ) : isFree ? (
          <button
            onClick={() => onUpgrade(plan)}
            data-testid={`plan-free-cta-${plan.plan_id}`}
            className="w-full py-3.5 rounded-2xl bg-[#14b8a6] text-black font-bold hover:scale-[1.02] transition-transform"
          >
            <i className={`fas ${icon} mr-2`} /> {plan.audience === "player" ? "START CASUAL PROFILE" : "BUILD FREE LEAGUE PAGE"}
          </button>
        ) : (
          <button
            onClick={() => onUpgrade(plan)}
            data-testid={`upgrade-btn-${plan.plan_id}`}
            className="w-full py-3.5 rounded-2xl btn-neon relative overflow-hidden"
          >
            {plan.audience === "player"
              ? `ACTIVATE PRO PLAYER • $${plan.price_usd}/mo`
              : `GO PRO LEAGUE • $${plan.price_usd}/mo`}
            <StripeLiveChip />
          </button>
        )}
      </div>

      {plan.plan_id === "league-pro" && (
        <p data-testid="referral-hook" className="mt-4 text-center text-xs text-[#14b8a6] font-bold leading-relaxed">
          🏟 League owners get the gold trim + custom badge + AI banners
        </p>
      )}
    </div>
  );
}

function PlanGroup({ title, subtitle, badge, plans, current, onUpgrade, sectionId, icons, dataTestid }) {
  return (
    <div id={sectionId} className="mb-16 last:mb-0">
      <div className="text-center mb-8">
        <span className="px-4 py-1.5 text-[10px] font-bold tracking-[3px] bg-[#14b8a6]/15 text-[#14b8a6] rounded-full neon-border-[#14b8a6]">
          {badge}
        </span>
        <h3 className="text-3xl md:text-5xl font-display grad-text mt-5">{title}</h3>
        <p className="text-base md:text-lg text-[#a3a3ac] mt-3">{subtitle}</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-testid={dataTestid}>
        {plans.map((p, idx) => (
          <PlanCard
            key={p.plan_id}
            plan={p}
            isCurrent={current.includes(p.plan_id)}
            onUpgrade={onUpgrade}
            numeral={idx + 1}
            icon={icons[idx] || "fa-bolt"}
          />
        ))}
      </div>
    </div>
  );
}

export function PricingSection({ onToast, onRequireAuth }) {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [pollMsg, setPollMsg] = useState("");

  useEffect(() => { fetchPlans().then(setPlans); }, []);

  // On return from Stripe, poll checkout status until paid or failed.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      onToast?.("Checkout cancelled.");
      const url = window.location.pathname;
      window.history.replaceState({}, "", url);
      return;
    }
    if (!sid || !user) return;
    let attempts = 0;
    setPollMsg("⌛ Verifying payment with Stripe…");
    const tick = async () => {
      attempts += 1;
      try {
        const res = await getCheckoutStatus(sid);
        if (res.payment_status === "paid" && res.plan_applied) {
          setPollMsg(`✅ ${res.package_id === "pro_player" ? "PRO PLAYER" : "PRO LEAGUE"} ACTIVATED.`);
          onToast?.(res.package_id === "pro_player"
            ? "🔥 PRO PLAYER activated. Banner, avatar upload + AI gen unlocked."
            : "🚀 PRO LEAGUE activated. Tournaments + AI banners unlocked.");
          if (refresh) await refresh();
          const url = window.location.pathname;
          window.history.replaceState({}, "", url);
          return;
        }
        if (res.status === "expired" || res.payment_status === "failed") {
          setPollMsg("⚠️ Payment did not complete.");
          return;
        }
        if (attempts >= 8) {
          setPollMsg("Still processing. Check your email — we'll confirm shortly.");
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        setPollMsg(e?.response?.data?.detail || "Couldn't verify status. Retry.");
      }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const [pendingPlan, setPendingPlan] = useState(null);

  const onUpgrade = async (plan) => {
    if (!user) return onRequireAuth();
    const key = plan.plan_id === "player-pro" ? "pro_player"
      : plan.plan_id === "league-pro" ? "pro_league"
      : null;
    if (!key) {
      onToast("That's the FREE tier — you already have it. Drop in!");
      return;
    }
    // Gate every paid upgrade behind the legal-consent modal. If the
    // user already accepted the current version of the legal docs we
    // skip straight to Stripe. Bump LEGAL_CONFIG.versions to force a
    // fresh acceptance after material changes.
    const prior = readLegalConsent();
    const acceptedCurrent =
      prior &&
      prior.versions?.terms === LEGAL_CONFIG.versions.terms &&
      prior.versions?.privacy === LEGAL_CONFIG.versions.privacy &&
      prior.versions?.subscriptions === LEGAL_CONFIG.versions.subscriptions;
    if (!acceptedCurrent) {
      setPendingPlan(plan);
      return;
    }
    await launchCheckout(key);
  };

  const launchCheckout = async (key) => {
    try {
      // Real Stripe Subscriptions (monthly auto-renewal) — both PRO Player
      // and PRO League are recurring. Gifts + Superboost stay on the
      // one-time createCheckout path (separate components).
      const res = await createSubscription(key, window.location.origin);
      if (res?.url) {
        window.location.href = res.url;
      } else {
        onToast?.("Couldn't start checkout — retry.");
      }
    } catch (e) {
      onToast(e?.response?.data?.detail || "Upgrade failed.");
    }
  };

  const playerPlans = plans.filter((p) => p.audience === "player");
  const leaguePlans = plans.filter((p) => p.audience === "league");

  const current = plans
    .filter((p) => {
      if (!user) return false;
      if (p.plan_id === "player-pro") return user.user_type === "pro" || user.plan === "pro";
      if (p.plan_id === "player-free") return !(user.user_type === "pro" || user.plan === "pro");
      if (p.plan_id === "league-pro") return user.plan === "elite";
      if (p.plan_id === "league-free") return user.plan !== "elite";
      return false;
    })
    .map((p) => p.plan_id);

  return (
    <section
      id="pricing"
      className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 page-z border-t border-[#14b8a6]/20"
    >
      <div className="text-center mb-14">
        <span className="px-4 py-1.5 text-[10px] font-bold tracking-[3px] bg-[#14b8a6]/15 text-[#14b8a6] rounded-full neon-border-[#14b8a6]">
          NEON TIERS
        </span>
        <h2 className="text-3xl sm:text-5xl md:text-7xl font-display grad-text mt-5">PRICING</h2>
        <p className="text-lg text-[#a3a3ac] mt-4 max-w-2xl mx-auto">
          Free to play, free to host. PRO tiers unlock AI tools, banners,
          tournaments, and the viral free-tier hook.
        </p>
        {pollMsg && (
          <p data-testid="pricing-poll-status" className="mt-4 text-sm text-[#14b8a6] font-bold">
            {pollMsg}
          </p>
        )}
      </div>

      <PlanGroup
        title="OPERATOR TIERS"
        subtitle="FREE Casual Profile · $1.99/mo PRO PLAYER"
        badge="FOR PLAYERS"
        plans={playerPlans}
        current={current}
        onUpgrade={onUpgrade}
        sectionId="player-tiers"
        icons={["fa-user-astronaut", "fa-wand-magic-sparkles"]}
        dataTestid="player-pricing-grid"
      />

      <PlanGroup
        title="HOSTING TIERS"
        subtitle="FREE Casual League · $9.99/mo PRO LEAGUE"
        badge="FOR LEAGUE MANAGERS"
        plans={leaguePlans}
        current={current}
        onUpgrade={onUpgrade}
        sectionId="league-tiers"
        icons={["fa-trophy", "fa-rocket"]}
        dataTestid="league-pricing-grid"
      />

        {/* PricingSection wrapper — the `mt-14` keeps spacing between plans + redeem. */}
        <div className="mt-14">
          <RedeemCodePanel onToast={onToast} onRequireAuth={onRequireAuth} />
        </div>
      <CheckoutConsentModal
        plan={pendingPlan}
        onCancel={() => setPendingPlan(null)}
        onConfirm={() => {
          const key = pendingPlan?.plan_id === "player-pro" ? "pro_player"
            : pendingPlan?.plan_id === "league-pro" ? "pro_league"
            : null;
          setPendingPlan(null);
          if (key) launchCheckout(key);
        }}
      />
    </section>
  );
}

export default PricingSection;
